import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import '@mdxeditor/editor/style.css';
import { MDXEditor, toolbarPlugin, headingsPlugin, listsPlugin, quotePlugin, UndoRedo, BoldItalicUnderlineToggles, ListsToggle } from '@mdxeditor/editor';
import { Add, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import { Stack, Typography, Button, Box, Table, TableBody, TableRow, TableCell, Chip, IconButton, Dialog, DialogTitle, DialogContent, FormControl, FormLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio, TextField, Switch, DialogActions } from '@mui/material';
import { Card } from '@pimo/pimo-components';
import { useState } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const NotificationsManager = ({ notifications, type, triggers = [], recipientLists = [], fireEvent, }) => {
    var _a, _b, _c, _d, _e;
    const [openForm, setOpenForm] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [formData, setFormData] = useState({
        channel: "banner",
        title: "",
        text: "",
        recipients: "",
        recipientType: "emails",
        publishBannerNotification: false,
        triggers: type === "scheduled" ? [] : undefined,
    });
    const variables = (_b = (_a = triggers === null || triggers === void 0 ? void 0 : triggers.find((t) => { var _a; return t.name === ((_a = formData.triggers) === null || _a === void 0 ? void 0 : _a[0]); })) === null || _a === void 0 ? void 0 : _a.variables) !== null && _b !== void 0 ? _b : [];
    const handleOpenForm = () => {
        setFormData({
            channel: "banner",
            title: "",
            text: "",
            recipients: "",
            recipientType: "emails",
            publishBannerNotification: false,
            triggers: type === "scheduled" ? [] : undefined,
        });
        setOpenForm(true);
    };
    const handleEdit = (notification) => {
        if (!notification.id) {
            return;
        }
        setFormData(notification);
        setOpenForm(true);
    };
    const handleCloseForm = () => {
        setOpenForm(false);
    };
    const handleSubmit = () => {
        if (formData.id != null) {
            fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("notificationUpdated", Object.assign({}, formData));
        }
        else {
            fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("notificationCreated", Object.assign(Object.assign({}, formData), { createdAt: new Date().toISOString() }));
        }
        handleCloseForm();
    };
    const handleDelete = (notification) => {
        setOpenDelete(true);
        setFormData(notification);
    };
    const handleConfirmDelete = () => {
        if (formData.id !== null) {
            fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("notificationDeleted", formData);
        }
        setOpenDelete(false);
    };
    const handleCloseDelete = () => {
        setOpenDelete(false);
    };
    const notificationPublishLabel = (notification) => {
        var _a, _b;
        let label = "";
        if (notification.channel === "banner" &&
            notification.publishBannerNotification) {
            label =
                "Published: " +
                    new Date((_a = notification.publishedBannerAt) !== null && _a !== void 0 ? _a : "").toLocaleDateString();
        }
        else if (notification.channel === "banner" &&
            !notification.publishBannerNotification) {
            label = "Not published";
        }
        else {
            label =
                "Sent: " + new Date((_b = notification.createdAt) !== null && _b !== void 0 ? _b : "").toLocaleDateString();
        }
        return label;
    };
    const getNotificationButtonLabel = () => {
        if (type === "scheduled")
            return "Save";
        if (formData.channel === "email")
            return "Send";
        if (formData.channel === "banner") {
            return formData.publishBannerNotification ? "Publish" : "Save";
        }
        return "Publish";
    };
    return (jsxs(Card, { sx: { width: "100%", p: 2, boxShadow: "none" }, children: [jsxs(Stack, { direction: "row", justifyContent: "flex-end", alignItems: "center", mb: 2, sx: {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "white",
                    padding: "16px 0",
                }, children: [jsxs(Typography, { variant: "h5", sx: { flexGrow: 1 }, children: [type === "normal" && "Notifications", type === "scheduled" && "Scheduled Notifications"] }), jsxs(Button, { variant: "outlined", onClick: handleOpenForm, startIcon: jsx(Add, {}), children: [type === "normal" && "New Notification", type === "scheduled" && "New Scheduled Notification"] })] }), notifications.length === 0 && (jsx(Typography, { sx: {
                    color: "#979797",
                    fontSize: "30px",
                    fontWeight: 500,
                    display: "block",
                    margin: "auto",
                    marginTop: "150px",
                    marginBottom: "150px",
                }, children: "You have not yet created any notifications." })), notifications.length > 0 && (jsx(Box, { sx: {
                    overflowY: "auto",
                    maxHeight: "700px",
                    marginLeft: "24px",
                }, children: jsx(Table, { children: jsx(TableBody, { children: notifications.map((notification, index) => {
                            var _a, _b, _c, _d, _e, _f, _g;
                            return (jsxs(TableRow, { children: [jsx(TableCell, { children: index + 1 }), jsxs(TableCell, { sx: { width: "40%" }, children: [jsx("strong", { children: notification.title }), notification.text ? `: ${notification.text}` : ""] }), jsxs(TableCell, { align: "right", children: [type === "scheduled" &&
                                                ((_a = notification.triggers) === null || _a === void 0 ? void 0 : _a.length) > 0 && (jsx(Chip, { label: `Triggers: ${(_b = notification.triggers) === null || _b === void 0 ? void 0 : _b.join(",")}`, color: "secondary", sx: { color: "#000", mr: 1, fontWeight: 500 } })), type === "normal" && (jsx(Chip, { label: `Source: ${(_c = notification.source) !== null && _c !== void 0 ? _c : "user"}`, sx: { color: "#000", mr: 1, fontWeight: 500 }, color: "default" })), jsx(Chip, { label: ((_e = (_d = notification.channel) === null || _d === void 0 ? void 0 : _d[0].toUpperCase()) !== null && _e !== void 0 ? _e : "") +
                                                    ((_g = (_f = notification.channel) === null || _f === void 0 ? void 0 : _f.slice(1)) !== null && _g !== void 0 ? _g : ""), sx: {
                                                    backgroundColor: "#EDE7F6",
                                                    color: "#673AB7",
                                                    mr: 1,
                                                    fontWeight: 500,
                                                } }), jsx(Chip, { label: `Recipients: ${notification.recipients ? `${notification.recipients.split(",").length}` : "all"}`, sx: { color: "#000", mr: 1, fontWeight: 500 }, color: "warning" }), type === "normal" && (jsx(Chip, { label: notificationPublishLabel(notification), sx: {
                                                    backgroundColor: "rgba(0, 200, 83, 0.2)",
                                                    color: "#00C853",
                                                    marginRight: 2,
                                                    fontWeight: 500,
                                                } })), jsx(IconButton, { size: "small", color: "primary", disabled: notification.channel === "email" && type === "normal", onClick: () => handleEdit(notification), children: jsx(EditOutlined, {}) }), jsx(IconButton, { size: "small", color: "error", disabled: notification.channel === "email" && type === "normal", onClick: () => handleDelete(notification), children: jsx(DeleteOutlined, {}) })] })] }, notification.id));
                        }) }) }) })), jsxs(Dialog, { open: openForm, onClose: handleCloseForm, sx: { margin: 8, minWidth: "800px" }, fullScreen: true, PaperProps: { sx: { borderRadius: 5, width: "100%" } }, children: [jsxs(DialogTitle, { sx: { color: "#000000" }, children: [type === "normal" &&
                                (formData.id != null ? "Edit Notification" : "New Notification"), type === "scheduled" &&
                                (formData.id != null
                                    ? "Edit Scheduled Notification"
                                    : "New Scheduled Notification")] }), jsxs(DialogContent, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            flex: "1 1 100%",
                            width: "100%",
                            gap: "24px",
                        }, children: [type === "scheduled" && (jsx(FormControl, { sx: { flex: "1 1 100%" }, children: jsxs(Fragment, { children: [jsx(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: "Trigger" }), jsx(Select, { value: (_c = formData.triggers) === null || _c === void 0 ? void 0 : _c[0], onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { triggers: [e.target.value] })), children: triggers.map((trigger) => (jsx(MenuItem, { value: trigger.name, children: trigger.name }, trigger.name))) })] }) })), jsxs(FormControl, { disabled: formData.id != null && type === "normal", children: [jsx(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: "Type of Notification" }), jsxs(RadioGroup, { row: true, value: formData.channel, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { publishBannerNotification: false, channel: e.target.value })), children: [jsx(FormControlLabel, { value: "banner", control: jsx(Radio, {}), label: "Banner" }), jsx(FormControlLabel, { value: "notification", control: jsx(Radio, {}), label: "Notification" }), jsx(FormControlLabel, { value: "email", control: jsx(Radio, {}), label: "Email" })] })] }), jsx(TextField, { label: formData.channel === "email" ? "Subject" : "Title", value: formData.title, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { title: e.target.value })), fullWidth: true, margin: "normal" }), jsx(Box, { sx: {
                                    border: "1px solid #ddd",
                                    borderRadius: "8px",
                                    padding: "8px",
                                    backgroundColor: "#fff",
                                    "& ._toolbarRoot_uazmk_160": {
                                        backgroundColor: "white",
                                    },
                                }, children: jsx(MDXEditor, { markdown: formData.text, onChange: (text) => setFormData(Object.assign(Object.assign({}, formData), { text })), plugins: [
                                        toolbarPlugin({
                                            toolbarContents: () => (jsxs(Fragment, { children: [jsx(UndoRedo, {}), jsx(BoldItalicUnderlineToggles, {}), jsx(ListsToggle, {})] })),
                                        }),
                                        headingsPlugin(),
                                        listsPlugin(),
                                        quotePlugin(),
                                    ] }) }), recipientLists.length > 0 && (jsxs(FormControl, { children: [jsx(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: "Send notification to..." }), jsxs(RadioGroup, { row: true, value: (_d = formData.recipientType) !== null && _d !== void 0 ? _d : "emails", onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { recipientType: e.target
                                                .value, recipients: "" })), children: [jsx(FormControlLabel, { value: "emails", control: jsx(Radio, {}), label: "Individual users (emails)" }), jsx(FormControlLabel, { value: "recipientList", control: jsx(Radio, {}), label: "A predefined recipient list" })] })] })), (formData.recipientType === "emails" || !formData.recipientType) && (jsx(TextField, { label: "Recipients", helperText: "Comma-separated list of email addresses of users who will see this notification. Leave empty to send to all users.", value: formData.recipients, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { recipients: e.target.value })), fullWidth: true, margin: "normal" })), formData.recipientType === "recipientList" && (jsxs(FormControl, { children: [jsx(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: "Recipient list" }), jsx(Select, { value: formData.recipients, onChange: (e) => setFormData(Object.assign(Object.assign({}, formData), { recipients: e.target.value })), children: recipientLists.map((list) => (jsx(MenuItem, { value: list, children: list }, list))) })] })), type === "scheduled" && variables.length > 0 && (jsxs(Fragment, { children: ["Available variables in subject and text:", " ", variables.map((variable) => (jsxs("li", { children: [jsx("pre", { style: { display: "inline" }, children: variable.name }), "\u00A0 (", variable.description, ")"] })))] })), formData.channel === "banner" && (jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [jsx(Switch, { checked: formData.publishBannerNotification, onChange: () => {
                                            setFormData(Object.assign(Object.assign({}, formData), { publishBannerNotification: !formData.publishBannerNotification }));
                                        } }), jsx(Typography, { sx: { ml: 2 }, children: formData.publishBannerNotification ? "Publish" : "Save" })] }))] }), jsxs(DialogActions, { children: [jsx(Button, { onClick: handleCloseForm, children: "Cancel" }), jsx(Button, { variant: "contained", onClick: handleSubmit, 
                                /**
                                 * once we finish the implementation of the new theme and components, we can replace the color with the new theme color
                                 */
                                sx: {
                                    color: "#ffffff",
                                }, disabled: !formData.title ||
                                    !formData.channel ||
                                    (type === "scheduled" && !((_e = formData.triggers) === null || _e === void 0 ? void 0 : _e[0])), children: getNotificationButtonLabel() })] })] }), jsxs(Dialog, { open: openDelete, onClose: handleCloseDelete, children: [jsx(DialogTitle, { children: "Confirm Delete" }), jsx(DialogContent, { children: jsx(Typography, { children: "Are you sure you want to delete this notification?" }) }), jsxs(DialogActions, { children: [jsx(Button, { onClick: handleCloseDelete, children: "Cancel" }), jsx(Button, { variant: "contained", color: "error", onClick: handleConfirmDelete, children: "Delete" })] })] })] }));
};

class NotificationsPlugin {
    /**
     * @param _strapiBaseUrl - base URL of Strapi backend
     * @param _interval - interval in milliseconds to check for new notifications
     */
    constructor(_strapiBaseUrl, _interval = 30000) {
        this._strapiBaseUrl = _strapiBaseUrl;
        this._interval = _interval;
    }
    onRegister(app) {
        this.app = app;
        app.patchAppState({
            banner: undefined,
            allNotificationsForUser: [],
        });
        app.on("init", () => {
            void this.fetchUnreadNotificationsIntoAppState();
            setInterval(() => void this.fetchUnreadNotificationsIntoAppState(), this._interval);
        });
    }
    fetchUnreadNotificationsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const unreadNotifications = yield this.fetchUnreadNotifications();
            // Split notifications by channel type
            const bannerNotifications = unreadNotifications.filter((n) => n.channel === "banner");
            const regularNotifications = unreadNotifications.filter((n) => n.channel === "notification");
            const latestBanner = bannerNotifications.length > 0
                ? bannerNotifications.sort((n1, n2) => { var _a, _b, _c; return (_c = (_a = n2.createdAt) === null || _a === void 0 ? void 0 : _a.localeCompare((_b = n1.createdAt) !== null && _b !== void 0 ? _b : "")) !== null && _c !== void 0 ? _c : 1; })[0]
                : undefined;
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                banner: latestBanner,
                allNotificationsForUser: regularNotifications,
            });
        });
    }
    fetchAllNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/all`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchAllNotificationsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const allNotifications = yield this.fetchAllNotifications();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                allNotifications,
            });
        });
    }
    fetchAllNotificationsForUserIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const allNotificationsForUser = yield this.fetchAllNotificationsForUser();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                allNotificationsForUser,
            });
        });
    }
    fetchAllNotificationsForUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchScheduledNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchScheduledNotificationsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const scheduledNotifications = yield this.fetchScheduledNotifications();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                scheduledNotifications: scheduledNotifications,
            });
        });
    }
    fetchUnreadNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/unread`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchReadNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/read`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchTriggersIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const triggers = yield this.fetchTriggers();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                notificationTriggers: triggers,
            });
        });
    }
    fetchTriggers() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/triggers`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchRecipientListsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const recipientLists = yield this.fetchRecipientLists();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                notificationRecipientLists: recipientLists,
            });
        });
    }
    fetchRecipientLists() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/recipient-lists`, {
                    credentials: "include",
                });
                return (yield response.json());
            }
            catch (_a) {
                return [];
            }
        });
    }
    setNotificationToRead(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}/read`, {
                method: "POST",
                credentials: "include",
            });
        });
    }
    setNotificationToUnread(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}/unread`, {
                method: "POST",
                credentials: "include",
            });
        });
    }
    createNotification(notification) {
        return __awaiter(this, void 0, void 0, function* () {
            const transformedNotification = Object.assign(Object.assign({}, notification), { publishedBannerAt: notification.channel === "banner" &&
                    notification.publishBannerNotification
                    ? new Date()
                    : null, recipientType: notification.recipientType || "emails" });
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(transformedNotification),
            });
        });
    }
    createScheduledNotification(notification) {
        return __awaiter(this, void 0, void 0, function* () {
            const transformedNotification = Object.assign(Object.assign({}, notification), { recipientType: notification.recipientType || "emails" });
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(transformedNotification),
            });
        });
    }
    deleteNotification(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    deleteScheduledNotification(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled/${notificationId}`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    updateNotification(notificationId, notification) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}`, {
                method: "PUT",
                credentials: "include",
                body: JSON.stringify(notification),
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    updateScheduledNotification(notificationId, notification) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled/${notificationId}`, {
                method: "PUT",
                credentials: "include",
                body: JSON.stringify(notification),
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    getName() {
        return "NotificationsPlugin";
    }
    handleOverlayNotificationReadEvent(_a) {
        return __awaiter(this, arguments, void 0, function* ({ payload, }) {
            var _b, _c, _d, _e;
            const { notification } = payload;
            if (!(notification === null || notification === void 0 ? void 0 : notification.id))
                return;
            yield this.setNotificationToRead(notification.id);
            if (notification.channel === "banner") {
                (_b = this.app) === null || _b === void 0 ? void 0 : _b.patchAppState({ banner: undefined });
                void this.fetchUnreadNotificationsIntoAppState();
            }
            else if (notification.channel === "notification") {
                const currentNotifications = (_d = (_c = this.app) === null || _c === void 0 ? void 0 : _c.getAppState().allNotificationsForUser) !== null && _d !== void 0 ? _d : [];
                const updatedNotifications = currentNotifications.filter((n) => n.id !== notification.id);
                (_e = this.app) === null || _e === void 0 ? void 0 : _e.patchAppState({
                    allNotificationsForUser: updatedNotifications,
                });
            }
        });
    }
    renderNotificationsManager({ view, route, layoutProps, notificationType = "normal", }) {
        const notificationsManager = view.addComponent({
            component: NotificationsManager,
            layoutProps,
        });
        notificationsManager.mapState(({ allNotifications, scheduledNotifications, notificationTriggers, notificationRecipientLists, }) => ({
            notifications: notificationType === "normal"
                ? (allNotifications !== null && allNotifications !== void 0 ? allNotifications : [])
                : (scheduledNotifications !== null && scheduledNotifications !== void 0 ? scheduledNotifications : []),
            type: notificationType,
            triggers: notificationTriggers !== null && notificationTriggers !== void 0 ? notificationTriggers : [],
            recipientLists: notificationRecipientLists !== null && notificationRecipientLists !== void 0 ? notificationRecipientLists : [],
        }));
        notificationsManager.on("notificationCreated", (_a) => __awaiter(this, [_a], void 0, function* ({ payload: notification }) {
            if (!notification) {
                return;
            }
            if (notificationType === "normal" &&
                isNormalNotification(notification)) {
                yield this.createNotification(notification);
                void this.fetchUnreadNotificationsIntoAppState();
                yield this.fetchAllNotificationsIntoAppState();
                yield this.fetchAllNotificationsForUserIntoAppState();
            }
            else if (notificationType === "scheduled" &&
                isScheduledNotification(notification)) {
                yield this.createScheduledNotification(notification);
                yield this.fetchScheduledNotificationsIntoAppState();
            }
        }));
        notificationsManager.on("notificationDeleted", (_a) => __awaiter(this, [_a], void 0, function* ({ payload: notification }) {
            if ((notification === null || notification === void 0 ? void 0 : notification.id) == null) {
                return;
            }
            if (isNormalNotification(notification) &&
                notificationType === "normal") {
                yield this.deleteNotification(notification.id);
                yield this.fetchAllNotificationsIntoAppState();
            }
            else if (isScheduledNotification(notification) &&
                notificationType === "scheduled") {
                yield this.deleteScheduledNotification(notification.id);
                yield this.fetchScheduledNotificationsIntoAppState();
            }
        }));
        notificationsManager.on("notificationUpdated", (_a) => __awaiter(this, [_a], void 0, function* ({ payload: notification }) {
            if (!(notification === null || notification === void 0 ? void 0 : notification.id)) {
                return;
            }
            if (isNormalNotification(notification) &&
                notificationType === "normal") {
                yield this.updateNotification(notification.id, notification);
                yield this.fetchAllNotificationsIntoAppState();
            }
            else if (isScheduledNotification(notification) &&
                notificationType === "scheduled") {
                yield this.updateScheduledNotification(notification.id, notification);
                yield this.fetchScheduledNotificationsIntoAppState();
            }
        }));
        route.on("load", () => {
            void this.fetchAllNotificationsIntoAppState();
            void this.fetchScheduledNotificationsIntoAppState();
            void this.fetchTriggersIntoAppState();
            void this.fetchRecipientListsIntoAppState();
        });
    }
}
function isScheduledNotification(notification) {
    return notification.triggers != null;
}
function isNormalNotification(notification) {
    return !isScheduledNotification(notification);
}

export { NotificationsPlugin };
